import React, { useEffect, useState } from "react";
import { ArrowLeft, Upload } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import Utils from "../../utils";
import { notify } from "../../components/Common/Alerts";
import { decryptData } from "../../utils/crypto";
import { createCards, updateCards } from "../../services/Admin/cardsService";
import { getAllCardsList } from "../../services/Admin/commonService";
import Select from "react-select";
import {
  createCardsCategories,
  detailsCardsCategories,
  updateCardsCategories,
} from "../../services/Admin/cardsCategoriesService";
import CardCategories from ".";

const AddCardCategories = () => {
  const navigate = useNavigate();
  const { uuid } = useParams();

  const [formData, setFormData] = useState({
    card_id: "",
    category: "",
    multiplier: "",
    uuid: "",
  });
  const [errors, setErrors] = useState({
    card_id: "",
    category: "",
    multiplier: "",
  });
  const [btnLoading, setBtnLoading] = useState(false);
  const [cardList, setCardList] = useState([]);
  const [cardCategoriesDetails, setCardCategoriesDetails] = useState("");

  const validateField = (name, value) => {
    switch (name) {
      case "card_id":
        if (!value.trim()) return "Card is required";
        return "";

      case "category":
        if (!value.trim()) return "Category is required";
        return "";

      case "multiplier":
        if (!value) return "Multiplier is required";
        if (isNaN(value)) return "Multiplier must be a number";
        return "";

      default:
        return "";
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: validateField(name, value),
    }));
  };

  useEffect(() => {
    getAllCardsList()
      .then((response) => {
        const decrypted = decryptData(response.data.data);

        const formatted = decrypted.data.map((item) => ({
          value: item.id,
          label: item.name,
        }));

        setCardList(formatted);
      })
      .catch((err) => {
        notify.error(Utils.getErrorMessage(err));
      });
  }, []);

  useEffect(() => {
    if (uuid) {
      let data = {
        uuid: uuid,
      };
      detailsCardsCategories(data)
        .then((response) => {
          const decrypted = decryptData(response.data.data);
          setCardCategoriesDetails(decrypted.data);
        })
        .catch((err) => {
          notify.error(Utils.getErrorMessage(err));
        });
    }
  }, [uuid]);

  useEffect(() => {
    if (cardCategoriesDetails) {
      setFormData({
        card_id: cardCategoriesDetails?.card_id,
        category: cardCategoriesDetails?.category,
        multiplier: cardCategoriesDetails?.multiplier,
        uuid: cardCategoriesDetails?.uuid,
      });
    }
  }, [cardCategoriesDetails]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {
      card_id: validateField("card_id", formData.card_id),
      name: validateField("name", formData.name),
      category: validateField("category", formData.category),
      multiplier: validateField("multiplier", formData.multiplier),
    };

    setErrors(newErrors);
    Utils.focusError();
    const hasErrors = Object.values(newErrors).some((err) => err);
    if (hasErrors) return;

    try {
      setBtnLoading(true);
      const payload = {
        name: formData.name,
        card_id: formData.card_id,
        category: formData.category,
        multiplier: formData.multiplier,
        ...(formData?.uuid && {
          uuid: formData.uuid,
        }),
      };

      const apiCall = formData?.uuid
        ? updateCardsCategories
        : createCardsCategories;

      const response = await apiCall(payload);
      const decrypted = decryptData(response.data.data);
      notify.success(decrypted.message);
      setFormData({
        card_id: "",
        category: "",
        multiplier: "",
        uuid: "",
      });
      setErrors({
        card_id: "",
        category: "",
        multiplier: "",
      });
      navigate("/card-categories");
    } catch (err) {
      notify.error(Utils.getErrorMessage(err));
    } finally {
      setBtnLoading(false);
    }
  };

  const getSelectedOption = (list, value) =>
    list.find((opt) => String(opt.value) === String(value)) || null;

  return (
    <div className="pb-24 md:pb-0 transition-colors duration-300">
      <div className="w-full bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm p-5 md:p-8">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate("/card-categories")}
            className="p-2 -ml-2 text-slate-400 dark:text-slate-500 hover:text-slate-800 dark:hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <h2 className="text-xl md:text-2xl font-black text-slate-800 dark:text-white tracking-tight">
            {uuid ? "Update Card Categories" : "Add Card Categories"}
          </h2>
        </div>

        <div className="space-y-6 md:space-y-8">
          <div>
            <h3 className="text-[10px] md:text-xs font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-5 border-b border-slate-50 dark:border-slate-700/50 pb-2">
              Card Categories Information
            </h3>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6">
              <div className="space-y-5">
                <div className="space-y-1.5">
                  <label htmlFor="category" className="input-label">
                    Category<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="category"
                    className="input-field"
                    placeholder="Enter category"
                    value={formData?.category || ""}
                    onChange={handleChange}
                    maxLength={100}
                  />
                  {errors.category && (
                    <span className="text-danger">{errors.category}</span>
                  )}
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="multiplier" className="input-label">
                    Multiplier <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    name="multiplier"
                    className="input-field"
                    placeholder="Enter multiplier"
                    value={formData?.multiplier || ""}
                    onChange={handleChange}
                    maxLength={100}
                  />
                  {errors.multiplier && (
                    <span className="text-danger">{errors.multiplier}</span>
                  )}
                </div>
              </div>

              <div className="space-y-5">
                <div className="space-y-1.5">
                  <label
                    htmlFor="card_id"
                    className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase"
                  >
                    Card <span className="text-red-500">*</span>
                  </label>
                  <Select
                    options={cardList}
                    value={getSelectedOption(cardList, formData.card_id)}
                    onChange={(selected) => {
                      setFormData((prev) => ({
                        ...prev,
                        card_id: String(selected?.value ?? ""),
                      }));
                    }}
                    placeholder="Select Card"
                    isClearable
                    isSearchable
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-end gap-4 pt-6 mt-6 border-t border-slate-50 dark:border-slate-700/50">
            <button
              type="button"
              onClick={() => navigate("/card-categories")}
              className="flex-1 md:flex-none px-6 py-3 rounded-xl font-black text-xs text-slate-400 dark:text-slate-500"
            >
              Cancel
            </button>
            <button
              type="button"
              form="reward-form"
              className="flex-[2] md:flex-none px-8 py-3 bg-[#D4AF37] text-white rounded-xl font-black text-xs shadow-lg shadow-orange-200/50"
              onClick={handleSubmit}
              disabled={btnLoading}
            >
              {btnLoading && (
                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              )}
              {btnLoading
                ? "submitting..."
                : formData.uuid
                  ? "Update"
                  : "Submit"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddCardCategories;
